import { Scene } from '../Scene';
import { SceneManifest } from '../SceneManifest';
import { NavigationSystem, TiledNavMeshStreamer } from '../../navigation-mesh';
import { TileParseWorkerClient } from '../../workers/TileParseWorkerClient';
import { TiledBvh } from '../../spatial/bvh/TiledBvh';
import { BlasStore } from '../../spatial/bvh/BlasStore';
import { createTiledBvhGenerator } from '../../spatial/bvh/BvhGenerator';

/**
 * Construct nav infrastructure and attach it to the scene. Initial spatial-grid
 * tile loading is deferred to {@link loadNavigationInitialTiles} so the caller
 * can finish wiring terrain (which the BVH generator's terrain fetcher reads)
 * before the first tiles materialize.
 */
export async function loadNavigationMesh(
  scene: Scene,
  manifest: SceneManifest,
  spawnX: number = 0,
  spawnZ: number = 0,
  workerClient?: TileParseWorkerClient,
): Promise<void> {
  if (!manifest.navigationMesh) return;

  const navRef = manifest.navigationMesh;
  const resourceLoader = scene.ctx.resourceLoader;

  scene.navigation = new NavigationSystem(
    scene.boundingBox,
    navRef.settings.voxelSize,
    navRef.settings.maxNeighbourStepHeight,
  );

  const streamer = new TiledNavMeshStreamer(navRef, scene.navigation, resourceLoader, undefined, workerClient);
  streamer.startInitialTiles(spawnX, spawnZ);

  scene.spatialGrid = new TiledBvh(
    navRef.tilesX,
    navRef.tilesZ,
    navRef.tileWorldSize,
    navRef.worldMinX,
    navRef.worldMinZ,
    navRef.settings.voxelSize,
    createTiledBvhGenerator({
      archiveCache: streamer.getArchiveCache(),
      blasStore: new BlasStore(),
      resolveModel: (modelId) => scene.modelRegistry.get(modelId),
      // Route terrain mesh generation through the SAME `TileMeshClient` the
      // renderer uses — guarantees the BVH triangles match what's drawn
      // (resolution, seed, height-sampling all aligned). The terrain streamer
      // is set up after `loadNavigationMesh` returns, so we late-bind via
      // `scene.terrainStreamer` at tile-load time.
      fetchTerrainMesh: manifest.terrain
        ? async (tx, tz) => {
            const ts = scene.terrainStreamer;
            return ts ? ts.generateTerrainMeshForTile(tx, tz) : null;
          }
        : null,
      workerClient: workerClient ?? null,
    }),
  );

  scene.navMeshStreamer = streamer;
}

/**
 * Block on the inner 3×3 of spatial-grid tiles around (spawnX, spawnZ). Call
 * after the terrain streamer is in place so the BVH terrain fetcher resolves.
 */
export async function loadNavigationInitialTiles(
  scene: Scene,
  spawnX: number,
  spawnZ: number,
): Promise<void> {
  if (!scene.spatialGrid) return;
  await scene.spatialGrid.loadInitialTiles(spawnX, spawnZ);
}
